import React, { useEffect, useRef } from 'react';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import {
  Drawer,
  DrawerBody,
  DrawerHeader,
  Typography,
} from '@one-thd/sui-atomic-components';
import { ArrowForward, CreditCard } from '@one-thd/sui-icons';
import {
  useCommonContext,
  useExperienceContext,
  useOrderDetailsContext,
  useViewChargedDetailsContext,
} from '../../../hooks/useContexts';
import { ViewChargedDetailsContent } from './ViewChargedDetailsContent';
import { ViewChargedDetailsProvider } from '../../../contexts/ViewChargedDetailsProvider';
import { FeatureSwitches } from '../../../constants/feature-switches';
import { SelectedChargedLineItemsView } from './SelectedChargedLineItemsView';

const ViewChargedDetailsInner: React.FC = () => {
  const { isB2BCustomer } = useThdCustomer();
  const { isMobile, isTablet } = useCommonContext();
  const { isConsumerApp } = useExperienceContext();
  const { orderDetailsData } = useOrderDetailsContext();
  const {
    openChargedDetailsDrawer,
    setOpenChargedDetailsDrawer,
    currentLineItemsView,
    setCurrentLineItemsView,
  } = useViewChargedDetailsContext();

  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  const featureSwitchEnabled = (FeatureSwitches.enableChargedDetailsView ??
    false) as boolean;
  // Check if the order is not from an online origin
  const isOrderNotOnline = orderDetailsData?.orderOrigin !== 'Online';
  // Check if the order is older than 25 months, then don't show the charge details view
  const isOrderPast25Months = orderDetailsData?.salesDate
    ? new Date(orderDetailsData.salesDate) <
      new Date(new Date().setMonth(new Date().getMonth() - 25))
    : false;

  const handleOnClose = () => {
    setOpenChargedDetailsDrawer(false);
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    closeTimeoutRef.current = setTimeout(() => {
      setCurrentLineItemsView(undefined);
      closeTimeoutRef.current = null;
    }, 400);
  };

  if (
    !isB2BCustomer ||
    isOrderNotOnline ||
    isOrderPast25Months ||
    isConsumerApp ||
    isMobile ||
    isTablet ||
    !featureSwitchEnabled
  )
    return null;

  return (
    <ErrorBoundary name="order-details-charged-details">
      <button
        type="button"
        className="sui-border-2 sui-border-solid sui-border-primary sui-rounded-lg sui-p-4 sui-inline-flex sui-w-full sui-items-center sui-cursor-pointer sui-transition hover:sui-bg-hover"
        onClick={() => {
          if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
          }
          setOpenChargedDetailsDrawer(true);
        }}
        onKeyDown={(press) => {
          if (press.key === 'Enter' || press.key === ' ') {
            if (closeTimeoutRef.current) {
              clearTimeout(closeTimeoutRef.current);
              closeTimeoutRef.current = null;
            }
            setOpenChargedDetailsDrawer(true);
          }
        }}
      >
        <CreditCard size="large" />
        <div className="sui-flex sui-flex-col sui-items-start sui-w-full sui-px-6">
          <Typography variant="h4">View Charge History</Typography>
          <Typography variant="body-base">
            See the breakdown of your charges.
          </Typography>
        </div>
        <ArrowForward size="small" />
      </button>
      <Drawer
        open={openChargedDetailsDrawer}
        onClose={handleOnClose}
        position="right"
      >
        <DrawerHeader
          title={
            currentLineItemsView
              ? currentLineItemsView.transactionDetails
              : 'Charge History'
          }
          onBack={
            currentLineItemsView
              ? () => setCurrentLineItemsView(undefined)
              : undefined
          }
          onClose={handleOnClose}
        />
        <DrawerBody>
          {currentLineItemsView ? (
            <SelectedChargedLineItemsView />
          ) : (
            <ViewChargedDetailsContent />
          )}
        </DrawerBody>
      </Drawer>
    </ErrorBoundary>
  );
};

/**
 * Component to view the charge history details of an order.
 * @returns A React component wrapped with the ViewChargedDetailsProvider.
 */
export const ViewChargedDetails: React.FC = () => (
  <ViewChargedDetailsProvider>
    <ViewChargedDetailsInner />
  </ViewChargedDetailsProvider>
);
